import { useState, useEffect, useContext } from 'react';
import { StoreContext } from '../../store';
import './index.css';

import { ReactComponent as WalkspanLogo } from '../../assets/walkspan-logo-2.svg';
import { ReactComponent as Dropdown } from '../../assets/profile/cube-arrow.svg';
import { ReactComponent as UpArrow } from '../../assets/profile/up-arrow.svg';
import { ReactComponent as DownArrow } from '../../assets/profile/down-arrow.svg';

import { getProfileAnalytics } from '../../api';
import { isObjectEmpty } from '../../services';

import Graph from './graph';
import DatePickerRange from '../../utility/DatePickerRange';

const Profile = () => {
  const { state } = useContext(StoreContext);
  const { userDetails, isMobile } = state;

  const [cyclePeriod, setCyclePeriod] = useState('weekly');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [drOpen, setDrOpen] = useState(false);

  const updateDateRange = (period) => {
    const today = new Date();
    let startDate = new Date(today);
    if (period === 'weekly') {
      startDate.setDate(today.getDate() - 6);
    } else if (period === 'monthly') {
      startDate.setDate(today.getDate() - 30);
    }
    setDateRange([startDate, today]);
  };

  const format = (date) => {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const formatToISO = (dateStr) => {
    if (!dateStr) return null;
    const mm = String(dateStr.getMonth() + 1).padStart(2, '0');
    const dd = String(dateStr.getDate()).padStart(2, '0');
    const yyyy = dateStr.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
  };

  const fetchProfileAnalytics = async () => {
    try {
      // Convert MM/DD/YYYY to YYYY-MM-DD before passing to the API
      const startDate = formatToISO(dateRange[0]);
      const endDate = formatToISO(dateRange[1]);
      const response = await getProfileAnalytics(
        userDetails?.email,
        startDate,
        endDate,
      );
      setAnalyticsData(response);
    } catch (error) {
      console.error('Error fetching profile analytics:', error);
    }
  };

  const handleDateRangeChange = (newRange) => {
    const [start, end] = newRange;
    if (!start || !end) return;
    setDateRange([format(start), format(end)]);
  }

  useEffect(() => {
    if (!userDetails || isObjectEmpty(userDetails)) return;
    updateDateRange(cyclePeriod);
  }, [userDetails, cyclePeriod]);

  useEffect(() => {
    if (
      !userDetails ||
      isObjectEmpty(userDetails) ||
      !dateRange[0] ||
      !dateRange[1]
    ) return;
    fetchProfileAnalytics();
  }, [dateRange]);

  const cycleTab = [
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' },
  ];

  const handleCyclePeriodChange = (value) => {
    setCyclePeriod(value);
  };

  const totalGemsSearched = () => {
    if (analyticsData?.lifestyle_essentials_category_total) {
      const total = Object.values(analyticsData.lifestyle_essentials_category_total).reduce((acc, value) => {
        return acc + (value || 0);
      }, 0);
      return total;
    }
  };

  return (
    <div className="border-t-[30px] border-[#13283b] p-4">
      <div className="flex flex-col gap-4 px-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[24px] font-medium text-[#13283b]">Neighborhood Preference Profile</div>
            <div>Analytics Report</div>
          </div>
          <WalkspanLogo className="h-[60px] w-auto" />
        </div>
        <div className='px-2 sm:px-4'>
          <div className='grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-20'>
            <div className='lg:col-span-5 flex flex-col gap-4'>
              {/* Datepicker */}
              <div className='mx-auto md:m-0 flex items-center gap-2 border-[2px] border-[#13283b] rounded-lg w-[fit-content] p-2'>
                <DatePickerRange
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  isOpen={drOpen}
                  handleOpen={setDrOpen}
                  disable={true}
                />
                <Dropdown className='h-[16px] w-auto' onClick={() => setDrOpen(true)}/>
              </div>
              <div>
                <div className='bg-[#13283b] text-white font-medium rounded-[10px] p-2 text-sm sm:text-base'>
                  User Profile
                </div>
                <div className='rounded-lg mt-4 p-2 sm:p-4 shadow-lg'>
                  <div className='grid grid-cols-2 text-xs'>
                    <div className="p-2 border font-bold">Name</div>
                    <div className="p-2 border">
                      {userDetails?.first_name} {userDetails?.last_name}
                    </div>
                    <div className="p-2 border font-bold">Company</div>
                    <div className="p-2 border">{userDetails?.company?.name}</div>
                    <div className="p-2 border font-bold">Email</div>
                    <div className="p-2 border">{userDetails?.email}</div>
                    <div className="p-2 border font-bold">Subscription Plan</div>
                    <div className="p-2 border">{userDetails?.subscription?.plan}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className='lg:col-span-7 flex flex-col gap-4 mt-6 lg:mt-0'>
              <div className='mx-auto md:m-0 p-[6px] rounded-xl shadow-xl flex w-[fit-content]'>
                {cycleTab.map((tab) => (
                  <div
                    key={tab.value}
                    className={`py-1 px-2 rounded-lg cursor-pointer text-sm transition-colors duration-300 ${
                      cyclePeriod === tab.value ? 'bg-[#13283b] text-white' : ''
                    }`}
                    onClick={() => handleCyclePeriodChange(tab.value)}
                  >
                    {tab.label}
                  </div>
                ))}
              </div>
              <div>
                <div className='bg-[#13283b] text-white font-medium rounded-[10px] p-2 text-sm sm:text-base'>
                  User Summary
                </div>
                <div className='flex flex-col sm:flex-row gap-2 mt-4'>
                  <div className='flex-1 rounded-xl shadow-xl p-4 sm:p-8 py-5 sm:py-7 flex flex-col items-center justify-start'>
                    <div className='flex text-[32px] sm:text-[48px] font-medium items-baseline justify-center'>
                      <UpArrow />{analyticsData?.address_searches || 0}
                    </div>
                    <div className='text-xs sm:text-sm text-center'>Addresses searched</div>
                  </div>
                  <div className='flex-1 rounded-xl shadow-xl p-4 sm:p-8 py-5 sm:py-7 flex flex-col items-center justify-start'>
                    <div className='flex text-[32px] sm:text-[48px] font-medium items-baseline justify-center'>
                      <DownArrow />{totalGemsSearched() || 0}
                    </div>
                    <div className='text-xs sm:text-sm text-center'>Lifestyle essentials searched</div>
                  </div>
                  <div className='flex-1 rounded-xl shadow-xl p-4 sm:p-8 py-5 sm:py-7 flex flex-col items-center justify-start'>
                    <div className='flex text-[32px] sm:text-[48px] font-medium items-baseline justify-center'>
                      <DownArrow />{analyticsData?.sidewalk_character_usage || 0}
                    </div>
                    <div className='text-xs sm:text-sm text-center'>Sidewalk character searched</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='bg-[#13283b] text-white font-medium rounded-[10px] p-2 my-4 text-sm sm:text-base'>
            Lifestyle Essetials Searched
          </div>
          <Graph gemUsage={analyticsData?.lifestyle_essentials_usage} isMobile={isMobile} />
        </div>
      </div>
    </div>
  )
};

export default Profile;